/**
 * Store Zustand pour l'authentification
 * REUTILISABILITE : 100% - Compatible Next.js et React
 *
 * Ce store delegue toute la logique metier aux Use Cases
 * et ne gere que l'etat React (state management).
 */

import { create, type StateCreator } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { container } from '../../infrastructure/di';
import { tokenManager } from '@shared/infrastructure/security/TokenManager';
import { tokenStorage } from '@shared/infrastructure/storage/tokenStorage';
import type {
  UserProfile,
  LoginCredentials,
  TwoFactorVerification,
  UpdateProfileData,
  ChangePasswordData,
  ChangeEmailData,
  VerifyEmailChangeData,
} from '../../application/dto/dtos';

// Timer ID pour le refresh proactif en arriere-plan
let backgroundRefreshTimerId: ReturnType<typeof setInterval> | null = null;

/** Demarrer le refresh proactif en arriere-plan */
function startBackgroundRefresh(refreshFn: () => Promise<void>) {
  stopBackgroundRefresh();
  backgroundRefreshTimerId = setInterval(async () => {
    const accessToken = tokenManager.getAccessToken();
    if (accessToken && tokenManager.needsRefresh(accessToken, 300)) {
      try {
        await refreshFn();
      } catch {
        // Erreur silencieuse - le refresh au prochain appel API prendra le relais
      }
    }
  }, 60_000); // Verification toutes les 60 secondes
}

/** Arreter le refresh proactif */
function stopBackgroundRefresh() {
  if (backgroundRefreshTimerId !== null) {
    clearInterval(backgroundRefreshTimerId);
    backgroundRefreshTimerId = null;
  }
}

/**
 * Interface du store d'authentification
 */
export interface AuthState {
  // Etat
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True une fois la tentative de reconnexion silencieuse (bootstrapAuth) resolue
   * (succes ou echec). Positionne une seule fois par l'appel racine de l'app —
   * ne pas re-declencher depuis chaque composant consommateur de useAuth().
   */
  isHydrated: boolean;
  requires2FA: boolean;
  requires2FAConfiguration: boolean;
  twoFactorSessionData: {
    idUtilisateur?: string;
    sessionId?: string;
    methode2FA?: 'EMAIL' | 'APPLICATION';
  } | null;
  error: string | null;

  // Actions - Authentification
  login: (credentials: LoginCredentials) => Promise<void>;
  verify2FA: (verification: TwoFactorVerification) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;

  // Actions - Profil
  getProfile: () => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  changeEmail: (data: ChangeEmailData) => Promise<void>;
  verifyEmailChange: (data: VerifyEmailChangeData) => Promise<void>;

  // Actions - 2FA
  activate2FA: (methode: 'EMAIL' | 'APPLICATION', code: string, secret?: string) => Promise<string[]>;
  deactivate2FA: (motDePasse: string, code: string) => Promise<void>;
  verifyBackupCode: (data: { idUtilisateur: string; code: string }) => Promise<boolean>;

  // Actions - Utilitaires
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
  checkAuth: () => void;
  bootstrapAuth: () => Promise<void>;
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isHydrated: false,
  requires2FA: false,
  requires2FAConfiguration: false,
  twoFactorSessionData: null,
  error: null,
};

const storeImplementation: StateCreator<AuthState> = (set, get) => ({
  ...initialState,

  checkAuth: () => {
    const state = get();
    if (state.isAuthenticated && state.requires2FA && !state.twoFactorSessionData) {
      set({ requires2FA: false, requires2FAConfiguration: false, twoFactorSessionData: null });
      return;
    }
    if (!state.requires2FA && state.requires2FAConfiguration) {
      set({ requires2FAConfiguration: false });
      return;
    }
    if (!state.requires2FA && state.twoFactorSessionData) {
      set({ twoFactorSessionData: null });
    }
  },

  /**
   * Tentative de reconnexion silencieuse au chargement de l'app.
   * Contrat httpOnly v1.1 : l'access token vit en memoire JS uniquement et ne
   * survit jamais a un reload. Le seul etat persistant cote client est le
   * cookie refreshToken (httpOnly, invisible en JS). On tente donc toujours
   * un refresh au demarrage ; en cas d'echec (cookie absent/invalide), on
   * reste simplement deconnecte, sans appeler logout() ni afficher d'erreur
   * (cas normal d'un visiteur anonyme).
   */
  bootstrapAuth: async () => {
    if (get().isHydrated) return; // deja resolu — ne jamais relancer (StrictMode, remounts)
    set({ isLoading: true });
    try {
      await container.refreshTokenUseCase.execute();
      const userData = await container.getProfileUseCase.execute();
      set({ user: userData, isAuthenticated: true, isLoading: false });
      startBackgroundRefresh(() => get().refreshToken());
    } catch {
      // Echec du refresh (cookie absent/invalide) : ignorer tout etat isAuthenticated/user
      // persiste en localStorage par le middleware Zustand (residu pre-httpOnly) — seul le
      // cookie httpOnly fait foi, jamais l'etat client persiste.
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
    get().checkAuth();
    set({ isHydrated: true });
  },

  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    try {
      const result = await container.loginUseCase.execute({ credentials });

      if (result.requires2FA) {
        set({
          user: result.user.toProfile(),
          isAuthenticated: false,
          requires2FA: true,
          requires2FAConfiguration: result.requires2FAConfiguration,
          twoFactorSessionData: result.twoFactorSessionData,
          isLoading: false,
        });
        return;
      }

      set({
        user: result.user.toProfile(),
        isAuthenticated: true,
        requires2FA: false,
        requires2FAConfiguration: false,
        twoFactorSessionData: null,
        isLoading: false,
        error: null,
      });

      // Demarrer le refresh proactif en arriere-plan
      startBackgroundRefresh(() => get().refreshToken());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de connexion';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  verify2FA: async (verification: TwoFactorVerification) => {
    set({ isLoading: true, error: null });
    try {
      const result = await container.verify2FAUseCase.execute(verification);

      set({
        user: result.user.toProfile(),
        isAuthenticated: true,
        requires2FA: false,
        requires2FAConfiguration: false,
        twoFactorSessionData: null,
        isLoading: false,
      });

      // Demarrer le refresh proactif en arriere-plan
      startBackgroundRefresh(() => get().refreshToken());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de verification 2FA';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    // Nettoyer les tokens AVANT de declencher la redirection (via isAuthenticated: false)
    // pour que les tests E2E trouvent localStorage vide apres redirect
    tokenStorage.clearTokens();
    set({ isLoading: true, isAuthenticated: false });
    stopBackgroundRefresh();
    try {
      await container.logoutUseCase.execute();
    } finally {
      set({ ...initialState, isHydrated: true });
    }
  },

  refreshToken: async () => {
    try {
      await container.refreshTokenUseCase.execute();
    } catch (error) {
      await get().logout();
      throw error;
    }
  },

  getProfile: async () => {
    set({ isLoading: true, error: null });
    try {
      const userData = await container.getProfileUseCase.execute();

      set({
        user: userData,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de recuperation du profil';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateProfile: async (data: UpdateProfileData) => {
    set({ isLoading: true, error: null });
    try {
      const updatedUser = await container.updateProfileUseCase.execute(data);
      set({ user: updatedUser, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de mise a jour du profil';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  changePassword: async (data: ChangePasswordData) => {
    set({ isLoading: true, error: null });
    try {
      await container.changePasswordUseCase.execute(data);
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de changement de mot de passe';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  changeEmail: async (data: ChangeEmailData) => {
    set({ isLoading: true, error: null });
    try {
      await container.changeEmailUseCase.execute(data);
      await get().getProfile();
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de changement d\'email';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  verifyEmailChange: async (data: VerifyEmailChangeData) => {
    set({ isLoading: true, error: null });
    try {
      await container.verifyEmailChangeUseCase.execute(data);
      await get().getProfile();
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de verification du code';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  activate2FA: async (
    methode: 'EMAIL' | 'APPLICATION',
    code: string,
    secret?: string
  ): Promise<string[]> => {
    set({ isLoading: true, error: null });
    const user = get().user;
    if (!user) throw new Error('Utilisateur non connecte');

    try {
      const backupCodes = await container.activate2FAUseCase.execute({
        idUtilisateur: user.id,
        methode,
        codeVerification: code,
        secretTOTP: secret,
      });

      await get().getProfile();
      set({ isLoading: false });
      return backupCodes;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur d\'activation 2FA';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deactivate2FA: async (motDePasse: string, code: string) => {
    set({ isLoading: true, error: null });
    const user = get().user;
    if (!user) throw new Error('Utilisateur non connecte');

    try {
      await container.deactivate2FAUseCase.execute({
        idUtilisateur: user.id,
        motDePasse,
        codeVerification: code,
      });

      await get().getProfile();
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de desactivation 2FA';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  verifyBackupCode: async (data: { idUtilisateur: string; code: string }): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const isValid = await container.verifyBackupCodeUseCase.execute(data);
      set({ isLoading: false });
      return isValid;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de verification du code backup';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null }),
  reset: () => {
    stopBackgroundRefresh();
    set({ ...initialState });
  },
});

export const useAuthStore = create<AuthState>()(
  persist(
    storeImplementation,
    {
      name: 'caritas-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state: AuthState) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        requires2FA: state.requires2FA,
        requires2FAConfiguration: state.requires2FAConfiguration,
        twoFactorSessionData: state.twoFactorSessionData,
      }),
    }
  )
);
